package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.ProviderReadiness
import dev.lingvo.shared.WorkspaceProviderDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.components.ProviderMark
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LCircleXIcon
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.ds.LSecretField
import dev.lingvo.site.ds.LSwitch
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LZapIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.requestWorkspaceAction
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text

/**
 * Translation provider keys, held once for the whole workspace.
 *
 * Every project used to store its own, so a ten-project workspace pasted the same DeepL key ten
 * times — and rotating it was ten edits, nine of which get done. A project may still override this,
 * and when it does, a failing override fails rather than quietly falling back to the key here.
 *
 * **Readiness, not `enabled && hasKey`.** The server now answers "can this workspace translate with
 * this provider" in one four-state field, computed by the same code the shell and every project
 * settings page read. The page used to answer it itself from three booleans and a timestamp, and
 * got the interesting case wrong: a key that will not decrypt is stored, enabled, and unusable.
 */
@Page("/app/settings/providers")
@Layout(".components.layouts.AppLayout")
@Composable
fun WorkspaceProvidersPage() {
    val toasts = rememberToasts()
    val scope = rememberCoroutineScope()
    val workspaceContext = WorkspaceRequestContext.current()

    var reloadToken by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(0) }
    // Typed keys, held per provider until saved. Never pre-filled from the server — the server does
    // not return keys, and a field that looked pre-filled would invite someone to "save" a mask.
    val drafts = rememberWorkspaceAuthorityState(workspaceContext) { mutableStateMapOf<String, String>() }
    var saving by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<String?>(null) }
    var sweep by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<KeySweep?>(null) }

    val claim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.PROVIDERS)

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
            // Absent, not disabled: `ProviderRoutes` is owners and admins, so a translator's load could
            // only ever come back 403 — after a wait, reading as a fault rather than as a role.
            if (claim == null) {
                PageHeader(
                    t("app.settings.providers.translation_providers", "Translation providers"),
                    t(
                        "app.settings.providers.the_keys_machine_translation_uses",
                        "The keys machine translation uses.",
                    ),
                    mark = { LKeyIcon() },
                )
                LAlert(
                    title = t("app.settings.providers.provider_keys_are_managed_by_an", "Provider keys are managed by an owner or admin"),
                    description = t("app.settings.providers.provider_keys_are_spend_so_only", "Provider keys are spend, so only owners and admins can see or set them."),
                    variant = LAlertVariant.INFO,
                )
                return@Column
            }

            // Below the refusal, so the refused request is never made. `settings/Usage.kt` orders it
            // this way for the same reason: a load above the gate still goes out and still comes back
            // 403, which is the wait the check exists to remove.
            val reloadable = reloadWorkspaceProtectedResource(
                context = workspaceContext,
                privilege = WorkspacePrivilege.PROVIDERS,
                reloadToken = reloadToken,
                transport = ApiClient::workspaceProviders,
            )
            val loaded = (reloadable.state as? Async.Success)?.value.orEmpty()
            val testable = loaded.filter { it.hasKey && !it.keyUnreadable }

            PageHeader(
                t("app.settings.providers.translation_providers", "Translation providers"),
                tf(
                    "app.settings.providers.keys_used_by_every_project_in_workspace",
                    "Keys used by every project in {workspace}, unless a project overrides them.",
                    "workspace" to (workspaceContext?.workspace?.name
                        ?: t("app.settings.providers.this_workspace", "this workspace")),
                ),
                mark = { LKeyIcon() },
                meta = { storedKeyTestCostNote(testable.size)?.let { Text(it) } },
                status = {
                    // The count the page is about, stated rather than counted by eye off six cards.
                    LBadge(
                        tPlural(
                            "app.settings.providers.count.ready_provider",
                            loaded.count { it.readiness == ProviderReadiness.READY },
                            "{count} provider ready",
                            "{count} providers ready",
                        ),
                        variant = if (loaded.any { it.readiness == ProviderReadiness.READY }) {
                            LBadgeVariant.SUCCESS
                        } else {
                            LBadgeVariant.NEUTRAL
                        },
                    )
                },
                actions = {
                    // **The real count, and only when there is something to count.** "Test all keys" on
                    // a workspace with no stored key is a button whose whole job is to do nothing.
                    if (testable.isNotEmpty()) {
                        LButton(
                            tPlural(
                                "app.settings.providers.count.test_stored_key",
                                testable.size,
                                "Test the {count} stored key",
                                "Test the {count} stored keys",
                            ),
                            {
                                val running = sweep
                                if (running == null || running.done >= running.total) {
                                    sweep = KeySweep(total = testable.size)
                                    scope.launch {
                                        testable.forEach { provider ->
                                            val outcome = try {
                                                val result = requestWorkspaceAction(
                                                    claim = claim,
                                                    currentContext = WorkspaceRequestContext::current,
                                                ) { workspaceSlug ->
                                                    ApiClient.saveWorkspaceProvider(
                                                        orgSlug = workspaceSlug,
                                                        providerId = provider.provider.id,
                                                        // Null leaves the stored key alone: this is a
                                                        // test, not a write.
                                                        apiKey = null,
                                                        enabled = null,
                                                    )
                                                }
                                                KeyTestOutcome(
                                                    provider = provider.provider,
                                                    passed = result.testPassed,
                                                    message = result.testMessage,
                                                )
                                            } catch (cancelled: CancellationException) {
                                                throw cancelled
                                            } catch (failure: Throwable) {
                                                KeyTestOutcome(
                                                    provider = provider.provider,
                                                    passed = false,
                                                    message = failure.message?.takeIf(String::isNotBlank),
                                                )
                                            }
                                            sweep = sweep?.let { it.copy(results = it.results + outcome) }
                                        }
                                        reloadToken++
                                    }
                                }
                            },
                            leadingIcon = { LRefreshIcon() },
                            loading = sweep?.let { it.done < it.total } == true,
                        )
                    }
                },
            )

            sweep?.let { run -> KeySweepPanel(run) { sweep = null } }

            // A failed refresh leaves the last good list on screen with a notice beside it, rather than
            // replacing six configured providers with a red box.
            reloadable.refreshError?.let {
                LAlert(
                    t("app.settings.providers.that_refresh_didnt_land", "That refresh didn't land"),
                    t(
                        "app.settings.providers.what_is_below_is_the_last",
                        "What is below is the last answer the server gave. Nothing has been changed.",
                    ),
                    variant = LAlertVariant.WARNING,
                    dense = true,
                    live = true,
                )
            }

            LAsyncContent(
                reloadable.state,
                rows = 4,
                loadingLabel = t("app.settings.providers.loading_providers", "Loading providers"),
                subject = t("app.settings.providers.provider_keys", "provider keys"),
                onRetry = { reloadToken++ },
                gap = Space.XXL,
            ) { providers ->
                providers.forEach { provider ->
                    ProviderCard(
                        provider = provider,
                        draft = drafts[provider.provider.name].orEmpty(),
                        onDraft = { drafts[provider.provider.name] = it },
                        saving = saving == provider.provider.name,
                        onSave = { enabled ->
                            scope.launch {
                                saving = provider.provider.name
                                try {
                                    val result = requestWorkspaceAction(
                                        claim = claim,
                                        currentContext = WorkspaceRequestContext::current,
                                    ) { workspaceSlug ->
                                        ApiClient.saveWorkspaceProvider(
                                            orgSlug = workspaceSlug,
                                            providerId = provider.provider.id,
                                            apiKey = drafts[provider.provider.name]?.takeIf { it.isNotBlank() },
                                            enabled = enabled,
                                        )
                                    }
                                    drafts.remove(provider.provider.name)
                                    // Save and test are one action, so the toast reports both. A green
                                    // "Saved" on a key that does not work is how someone finds a
                                    // trailing newline three days later, mid-pretranslate.
                                    when (result.testPassed) {
                                        true -> toasts.show(
                                            tr("app.settings.providers.saved_and_tested_the_key_works", "Saved and tested — the key works."),
                                            LToastVariant.SUCCESS,
                                            4_000,
                                        )
                                        false -> toasts.show(
                                            trf(
                                                "app.settings.providers.saved_but_the_test_failed",
                                                "Saved, but the test failed: {reason}",
                                                "reason" to result.testMessage.orEmpty(),
                                            ),
                                            LToastVariant.DANGER,
                                            8_000,
                                        )
                                        null -> toasts.show(tr("app.settings.providers.saved", "Saved."), LToastVariant.SUCCESS, 4_000)
                                    }
                                    reloadToken++
                                } catch (cancelled: CancellationException) {
                                    // Switching workspace mid-save cancels this scope. Caught as a
                                    // failure it reported "Couldn't save." for a save nobody abandoned
                                    // and whose outcome this page can no longer see either way.
                                    throw cancelled
                                } catch (failure: Throwable) {
                                    toasts.show(
                                        failure.message?.takeIf(String::isNotBlank)
                                            ?: tr("app.settings.providers.couldnt_save", "Couldn't save."),
                                        LToastVariant.DANGER,
                                        TOAST_UNTIL_DISMISSED,
                                    )
                                } finally {
                                    saving = null
                                }
                            }
                        },
                    )
                }
            }
        }
    }
    ToastHost(toasts)
}

/** One "test the stored keys" run: how far it got, and what each key answered. */
internal data class KeySweep(
    val total: Int,
    val results: List<KeyTestOutcome> = emptyList(),
) {
    val done: Int get() = results.size
    val failed: Int get() = results.count { it.passed == false }
}

internal data class KeyTestOutcome(
    val provider: MtProvider,
    val passed: Boolean?,
    val message: String?,
)

/**
 * The receipt for "Test the N stored keys".
 *
 * Progress is a discrete readout and the outcome is per key, because "3 of 6 done, 1 failed" is the
 * only form of this sentence that tells somebody which key to go and fix.
 */
@Composable
private fun KeySweepPanel(sweep: KeySweep, onDismiss: () -> Unit) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LTaskProgress(
                done = sweep.done,
                total = sweep.total,
                failed = sweep.failed,
                label = t("app.settings.providers.testing_the_stored_keys", "Testing the stored keys"),
                detail = if (sweep.done >= sweep.total) {
                    tf(
                        "app.settings.providers.count_of_total_keys_answered",
                        "{done} of {total} keys answered.",
                        "done" to sweep.done.toString(),
                        "total" to sweep.total.toString(),
                    )
                } else {
                    null
                },
            )
            sweep.results.forEach { outcome ->
                Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                    if (outcome.passed == true) LCheckIcon() else LCircleXIcon()
                    Text(outcome.provider.displayName)
                    when (outcome.passed) {
                        true -> LBadge(
                            t("app.settings.providers.works", "Works"),
                            variant = LBadgeVariant.SUCCESS,
                        )
                        false -> LBadge(
                            t("app.settings.providers.refused", "Refused"),
                            variant = LBadgeVariant.DANGER,
                        )
                        null -> LBadge(
                            t("app.settings.providers.no_answer", "No answer"),
                            variant = LBadgeVariant.NEUTRAL,
                        )
                    }
                    outcome.message?.let { LMuted(it) }
                }
            }
            if (sweep.done >= sweep.total) {
                LButton(
                    t("app.settings.providers.hide_these_results", "Hide these results"),
                    onDismiss,
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                )
            }
        }
    }
}

/**
 * The four-state answer, in words, for a workspace credential.
 *
 * Shared with the project surfaces on purpose — `ProjectProviderGrid` and `ProjectProviderDetail`
 * call this and [providerReadinessBadge] rather than keeping a second copy of either. Two
 * vocabularies for one enum is how "Off" and "Disabled" end up on the same screen.
 */
internal fun providerReadinessWords(readiness: ProviderReadiness): String = when (readiness) {
    ProviderReadiness.READY -> tr("app.settings.providers.readiness_ready", "Ready")
    ProviderReadiness.NEEDS_SETUP -> tr("app.settings.providers.readiness_needs_setup", "Needs setup")
    ProviderReadiness.OFF -> tr("app.settings.providers.readiness_off", "Off")
    ProviderReadiness.UNAVAILABLE -> tr("app.settings.providers.readiness_unavailable", "Unavailable")
}

/**
 * What "Test the N stored keys" costs, in words, or null when there is nothing to test.
 *
 * The project provider grid states this beside its own test control; the workspace page stated
 * nothing, and inference filled the gap the wrong way. A workspace key calls the provider on the
 * workspace's own account, so the test spends none of Lingvo's credits — but somebody who has just
 * watched a pre-translate quote a credit estimate has every reason to assume any button that
 * reaches a provider is billed, and the honest reading of an unexplained button is "do not press
 * it". Null at zero rather than an empty string, because the sentence is about a control that is
 * itself absent at zero, and a promise about a button nobody can press is noise.
 *
 * Extracted from the header so the promise is asserted rather than eyeballed: it is the one line on
 * this page whose absence changes what a reader does.
 */
internal fun storedKeyTestCostNote(storedKeys: Int): String? = if (storedKeys <= 0) {
    null
} else {
    tr(
        "app.settings.providers.testing_a_stored_key_calls_the",
        "Testing a stored key calls the provider on this workspace's own account and spends no " +
            "Lingvo credits.",
    )
}

/** The badge colour for a readiness, kept beside the words so the two cannot drift apart. */
internal fun providerReadinessBadge(readiness: ProviderReadiness): LBadgeVariant = when (readiness) {
    ProviderReadiness.READY -> LBadgeVariant.SUCCESS
    ProviderReadiness.NEEDS_SETUP -> LBadgeVariant.WARNING
    ProviderReadiness.OFF -> LBadgeVariant.NEUTRAL
    ProviderReadiness.UNAVAILABLE -> LBadgeVariant.DANGER
}

/**
 * One provider, its readiness, and the one control that changes it.
 *
 * Internal rather than private so `ProvidersReadinessBrowserTest` mounts the production card with a
 * DTO instead of asserting against a copy of it.
 */
@Composable
internal fun ProviderCard(
    provider: WorkspaceProviderDto,
    draft: String,
    onDraft: (String) -> Unit,
    saving: Boolean,
    onSave: (Boolean) -> Unit,
) {
    val name = provider.provider.displayName
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                // The vendor's own colour, so a grid of six is recognised rather than read.
                ProviderMark(provider.provider, size = 32)
                // Sized, not left to the user agent's 1.5em: `LSectionTitle` exists to stop a
                // heading whose size depends on which element it happens to be.
                //
                // **`h2`, not `h3`.** The project-scoped grid draws its cards as `h3` because they
                // hang off that section's own `h2`; this page has no such section — `PageHeader`
                // emits the only other heading, an `h1` — so an `h3` here left a heading list of
                // one `h1` followed by six `h3`s with the level between them missing, and pressing
                // 2 for headings found nothing on the page at all.
                //
                // `displayName`, not `name.lowercase()`. The enum spelled Google Translate
                // "google" and MyMemory "mymemory" on the one page whose job is telling them apart.
                H2(Modifier.fontSize(TypeScale.Body).fontWeight(700).margin(0.px).toAttrs()) {
                    Text(name)
                }
                LBadge(
                    providerReadinessWords(provider.readiness),
                    variant = providerReadinessBadge(provider.readiness),
                )
                // Its own route, because a credential is a thing somebody links a colleague to.
                //
                // The visible word stays "Open" — the heading beside it says which provider — but a
                // screen reader reading the link list off this page got "Open" six times over, with
                // nothing to choose between them. `Link` takes no accessibleLabel, so the name goes
                // on as an attribute.
                Link(
                    path = "/app/settings/providers/${provider.provider.id}",
                    modifier = Modifier.attr(
                        "aria-label",
                        trf(
                            "app.settings.providers.open_provider",
                            "Open {provider}",
                            "provider" to name,
                        ),
                    ),
                ) { Text(t("app.settings.providers.open", "Open")) }
            }

            // The server's own sentence for the two states the card cannot explain by itself: a key
            // that will not decrypt, a provider the plan does not include. It knows why and this
            // page does not.
            //
            // **Not for OFF.** Off is already said by the badge at the top of the card and decided
            // by the switch at the bottom of it, so the alert in between was the third statement of
            // one fact — "Off", then a titled notice reading "Off / This provider is turned off for
            // the workspace", then the control that turns it on. Six of those, one per provider,
            // were the dominant texture of the page and cost three lines each at 390.
            provider.unavailableReason?.takeIf {
                provider.readiness != ProviderReadiness.READY && provider.readiness != ProviderReadiness.OFF
            }?.let {
                LAlert(
                    providerReadinessWords(provider.readiness),
                    it,
                    variant = if (provider.readiness == ProviderReadiness.UNAVAILABLE) {
                        LAlertVariant.DANGER
                    } else {
                        LAlertVariant.INFO
                    },
                    dense = true,
                )
            }

            // Loud, because it is unrecoverable without action and the only other signal is a 400
            // at translate time, mid-job.
            if (provider.keyUnreadable) {
                LAlert(
                    t("app.settings.providers.this_key_can_no_longer_be", "This key can no longer be decrypted"),
                    t("app.settings.providers.the_encryption_key_changed_or_a_2", "The encryption key changed, or a backup was restored without it. The stored " +
                        "key cannot be used — entering it again is the only fix."),
                    variant = LAlertVariant.DANGER,
                )
            }

            if (provider.provider.requiresKey) {
                Row(
                    Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
                    verticalAlignment = Alignment.Bottom,
                ) {
                    // A credential, in the field written for credentials: password type by default,
                    // a reveal control with an exact accessible name, and no browser autofill.
                    LSecretField(
                        draft,
                        onDraft,
                        label = tf(
                            "app.settings.providers.provider_api_key",
                            "{provider} API key",
                            "provider" to name,
                        ),
                        // The stored key is never sent back, so the field shows a hint rather than
                        // a value. Leaving it empty keeps whatever is stored.
                        placeholder = provider.keyHint
                            ?: t("app.settings.providers.paste_a_key", "Paste a key"),
                        modifier = Modifier.flexGrow(1),
                    )
                    LButton(
                        t("app.settings.providers.save_and_test", "Save and test"),
                        { onSave(true) },
                        variant = LButtonVariant.SECONDARY,
                        enabled = !saving,
                        loading = saving,
                        leadingIcon = { LZapIcon() },
                    )
                }
                LMuted(
                    if (provider.hasKey) {
                        t("app.settings.providers.a_key_is_stored_leave_the", "A key is stored. Leave the field empty to keep it.")
                    } else {
                        t("app.settings.providers.without_a_key_here_projects_fall", "Without a key here, projects fall back to Lingvo's — which spends this workspace's credits.")
                    },
                )

                // Gated on this card's own save. Ungated, a second toggle while the first request
                // was still in flight sent the opposite value, and whichever response landed last
                // decided what the workspace ended up with.
                LSwitch(
                    provider.enabled,
                    { enabled -> onSave(enabled) },
                    label = t("app.settings.providers.available_to_projects_in_this_workspace", "Available to projects in this workspace"),
                    enabled = !saving,
                )
            } else {
                // **One press, because there is nothing to type.** MyMemory and LibreTranslate need
                // no credential, and both sat behind an empty "API key" field and a "Save and test"
                // button that saved nothing — so the two providers a workspace can turn on for free
                // looked exactly as unconfigured as the four that need a paid key.
                LMuted(
                    t(
                        "app.settings.providers.this_provider_needs_no_key_turn",
                        "This provider needs no key. Turn it on and every project here can use it.",
                    ),
                )
                if (provider.enabled) {
                    LButton(
                        tf(
                            "app.settings.providers.turn_provider_off",
                            "Turn {provider} off",
                            "provider" to name,
                        ),
                        { onSave(false) },
                        variant = LButtonVariant.GHOST,
                        enabled = !saving,
                        loading = saving,
                    )
                } else {
                    LButton(
                        tf(
                            "app.settings.providers.turn_provider_on",
                            "Turn {provider} on",
                            "provider" to name,
                        ),
                        { onSave(true) },
                        variant = LButtonVariant.SECONDARY,
                        enabled = !saving,
                        loading = saving,
                        leadingIcon = { LZapIcon() },
                    )
                }
            }
        }
    }
}
